import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Edit, Radio, AlertTriangle, Clock, CheckCircle2, XCircle, History, FileText, ImageIcon, Route } from 'lucide-react';
import { useAsset, useAssetTimeline, useAssetIncidents, useAssetImages } from '@/api/hooks/useAssets';
import { AssetTraceTimeline } from '@/components/workflow/AssetTraceTimeline';
import { AssetImageGallery } from './components/AssetImageGallery';
import {
  useAssetMetadata,
  useMetadataSchemaForAssetClass,
  useMetadataCategoriesDropdown,
} from '@/api/hooks/useMetadata';
import DynamicMetadataForm, { DynamicMetadataFormSkeleton } from '@/components/metadata/DynamicMetadataForm';
import { ChangeEpcDialog } from './components/ChangeEpcDialog';
import { AuditHistoryDialog } from './components/AuditHistoryDialog';
import { StageActionsDropdown } from '@/components/workflow/StageActionsDropdown';
import { useAssetEngineState } from '@/api/hooks/useWorkflows';
import { getAssetStatusColor, getAssetStatusLabel } from '@/config/tenant';
import {
  cn,
  formatDate as formatDateI18n,
  formatDateTime as formatDateTimeI18n,
} from '@/lib/utils';
import { EpcText } from '@/components/common/EpcText';

// Incident type colors
const incidentColors: Record<number, string> = {
  0: 'text-orange-500', // Daño al Activo
  1: 'text-orange-500', // Daño al EPC
  2: 'text-yellow-500', // EPC Incorrecto
  3: 'text-red-500', // Crítico
  4: 'text-blue-500', // Sin EPC
  5: 'text-gray-500', // Otro
};

// Thin wrappers over the shared helpers, which resolve the Intl locale from the
// active i18n language — the page must not pin a locale of its own or dates keep
// rendering in Spanish after switching to English.
function formatDate(dateString: string | undefined): string {
  if (!dateString) return '-';
  try {
    return formatDateI18n(dateString);
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString: string | undefined): string {
  if (!dateString) return '-';
  try {
    return formatDateTimeI18n(dateString);
  } catch {
    return dateString;
  }
}

// Loading skeleton
function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-10" />
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
        <Skeleton className="h-64" />
      </div>
    </div>
  );
}

// Error state
function ErrorState({ message, t }: { message?: string; t: (key: string) => string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <AlertTriangle className="h-12 w-12 text-muted-foreground mb-4" />
      <h2 className="text-lg font-semibold mb-2">{t('detail.errorTitle')}</h2>
      <p className="text-sm text-muted-foreground mb-4">{message || t('detail.errorMessage')}</p>
      <Link to="/assets">
        <Button variant="outline">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('detail.backToList')}
        </Button>
      </Link>
    </div>
  );
}

export default function AssetDetailPage() {
  const { t } = useTranslation(['assets', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const [showEpcDialog, setShowEpcDialog] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);

  // Fetch asset data
  const { data: asset, isLoading, error } = useAsset(id || '', !!id);
  const { data: timeline, isLoading: timelineLoading } = useAssetTimeline(id || '', !!id);
  const { data: incidents, isLoading: incidentsLoading } = useAssetIncidents(id || '', !!id);
  const { data: images = [], isLoading: imagesLoading } = useAssetImages(id || '', !!id);
  // Engine snapshot — null = no workflow bound for this asset (TOCA legacy
  // path). When present, takes precedence over the legacy assetStatusName
  // badge so the displayed state matches whatever the dynamic-flows engine
  // most recently transitioned the asset into.
  const { data: engineState } = useAssetEngineState(id || null);

  // Fetch metadata for this asset
  const { data: assetMetadata, isLoading: metadataLoading } = useAssetMetadata(id || '', !!id);

  // Fetch metadata schema for the asset's asset class
  const assetClassId = asset?.assetClassId || 0;
  const { data: metadataSchema, isLoading: schemaLoading } = useMetadataSchemaForAssetClass(
    assetClassId,
    assetClassId > 0
  );

  // Fetch metadata categories for grouping
  const { data: metadataCategories } = useMetadataCategoriesDropdown();

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !asset) {
    return <ErrorState message={error?.message} t={t} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/assets">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{asset.assetId}</h1>
            <p className="text-muted-foreground">{asset.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {/*
            Phase 2 opt-in: renders the engine-driven dynamic action menu
            ONLY when a workflow binding exists for this asset's AssetClass.
            When no binding exists (the Phase 1 production reality for every
            TOCA tenant), the /v2/assets/{id}/transitions call returns 409
            and the dropdown renders nothing — zero visual change for TOCA
            users per ADR-015.
          */}
          <StageActionsDropdown assetId={String(asset.assetId)} />
          <Button variant="outline" onClick={() => setShowAuditDialog(true)}>
            <History className="mr-2 h-4 w-4" />
            {t('auditHistory.buttonLabel')}
          </Button>
          <Button variant="outline" onClick={() => navigate(`/assets/${id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            {t('actions.edit')}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Asset Details */}
        <Card>
          <CardHeader>
            <CardTitle>{t('detail.cardTitle')}</CardTitle>
            <CardDescription>{t('detail.cardDescription')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.codeLabel')}</p>
                <p className="text-sm">{asset.assetId}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.nameLabel')}</p>
                <p className="text-sm">{asset.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.statusLabel')}</p>
                <span className={cn(
                  'inline-flex items-center rounded-full px-2 py-1 text-xs font-medium',
                  getAssetStatusColor(asset.assetStatus)
                )}>
                  {engineState ? engineState.currentStateName : getAssetStatusLabel(asset.assetStatus)}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.locationLabel')}</p>
                <p className="text-sm">{asset.locationName || '-'}</p>
              </div>
              {asset.ownerClientName && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('detail.ownerClientLabel')}</p>
                  <span className="inline-flex items-center rounded-full border px-2 py-1 text-xs font-medium">
                    {asset.ownerClientName}
                  </span>
                </div>
              )}
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.createdDateLabel')}</p>
                <p className="text-sm">{formatDate(asset.createdDate)}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.updatedDateLabel')}</p>
                <p className="text-sm">{formatDate(asset.updatedDate)}</p>
              </div>
            </div>
            <Separator />
            <div>
              <p className="text-sm font-medium text-muted-foreground">{t('detail.descriptionLabel')}</p>
              <p className="text-sm">{asset.description || t('detail.noDescription')}</p>
            </div>
            {asset.comments && (
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('detail.commentsLabel')}</p>
                <p className="text-sm">{asset.comments}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* EPC Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className="h-5 w-5" />
              {t('detail.epcTitle')}
            </CardTitle>
            <CardDescription>{t('detail.epcDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {asset.epc ? (
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{t('detail.epcCodeLabel')}</p>
                  <EpcText value={asset.epc} className="text-sm" />
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowEpcDialog(true)}>
                  {t('detail.changeEpcButton')}
                </Button>
              </div>
            ) : (
              <div className="text-center py-4">
                <Radio className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">{t('detail.noEpc')}</p>
                <Button size="sm" onClick={() => setShowEpcDialog(true)}>
                  {t('detail.assignEpcButton')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Asset Images Gallery */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              {t('images.title', 'Images')}
            </CardTitle>
            <CardDescription>
              {t('images.galleryDescription', 'Photos and images of this asset')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {imagesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Skeleton className="h-48 w-full max-w-md" />
              </div>
            ) : (
              <AssetImageGallery
                assetId={String(asset.assetId)}
                images={images}
                editable={true}
              />
            )}
          </CardContent>
        </Card>

        {/* Custom Metadata Fields */}
        {(assetClassId > 0 || metadataLoading || schemaLoading) && (
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('detail.metadataTitle', 'Additional Information')}
              </CardTitle>
              <CardDescription>
                {t('detail.metadataDescription', 'Custom fields for this asset')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {metadataLoading || schemaLoading ? (
                <DynamicMetadataFormSkeleton />
              ) : metadataSchema && assetMetadata?.values && assetMetadata.values.length > 0 ? (
                <DynamicMetadataForm
                  schema={metadataSchema}
                  values={assetMetadata.values}
                  categories={metadataCategories}
                  readOnly={true}
                  showCategories={true}
                />
              ) : metadataSchema ? (
                <p className="text-sm text-muted-foreground">
                  {t('detail.noMetadataValues', 'No custom field values have been set for this asset.')}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('detail.noMetadataSchema', 'No custom fields defined for this asset class.')}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* H2 — workflow-aware journey (only for engine-bound assets; the
            legacy timeline below stays untouched for everything else). */}
        {engineState && id && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Route className="h-5 w-5" />
                {t('trace.title')}
              </CardTitle>
              <CardDescription>{t('trace.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <AssetTraceTimeline assetId={id} />
            </CardContent>
          </Card>
        )}

        {/* Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {t('detail.timeline')}
            </CardTitle>
            <CardDescription>{t('detail.timelineDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {timelineLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : timeline && timeline.length > 0 ? (
              <ScrollArea className="h-[250px]">
                <div className="space-y-4 pr-4">
                  {timeline.map((item, index) => (
                    <div key={item.id} className="flex gap-3">
                      <div className="flex flex-col items-center">
                        <div className="h-2 w-2 rounded-full bg-primary" />
                        {index < timeline.length - 1 && (
                          <div className="h-full w-px bg-border" />
                        )}
                      </div>
                      <div className="pb-4">
                        <p className="text-sm">{item.event}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDateTime(item.date)} - {item.user}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-4">
                <Clock className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('detail.noTimeline')}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Incidents */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {t('detail.incidentsTitle')}
            </CardTitle>
            <CardDescription>{t('detail.incidentsDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            {incidentsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : incidents && incidents.length > 0 ? (
              <ScrollArea className="h-[200px]">
                <div className="space-y-3">
                  {incidents.map((incident) => (
                    <div
                      key={incident.id}
                      className="flex items-start gap-3 rounded-md border p-3"
                    >
                      <AlertTriangle
                        className={cn(
                          'h-5 w-5 mt-0.5',
                          incidentColors[incident.typeCode] || 'text-gray-500'
                        )}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">{incident.type}</p>
                          {incident.resolved ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-600">
                              <CheckCircle2 className="h-3 w-3" />
                              {t('detail.incidentResolved')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-yellow-600">
                              <XCircle className="h-3 w-3" />
                              {t('detail.incidentPending')}
                            </span>
                          )}
                        </div>
                        {incident.notes && (
                          <p className="text-xs text-muted-foreground mt-1 truncate">
                            {incident.notes}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDateTime(incident.date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-4">
                <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">{t('detail.noIncidents')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Change EPC Dialog */}
      <ChangeEpcDialog
        open={showEpcDialog}
        onOpenChange={setShowEpcDialog}
        assetId={asset.assetId}
        currentEpc={asset.epc}
        assetName={asset.name}
      />

      {/* Audit History Dialog */}
      <AuditHistoryDialog
        open={showAuditDialog}
        onOpenChange={setShowAuditDialog}
        entityName="Asset"
        entityId={String(asset.assetId)}
        title={asset.name}
      />
    </div>
  );
}
